import React, { useState } from "react";
import { createUseStyles } from "react-jss";
import { Button, Drawer, IconButton, Toolbar } from "@mui/material";
import { AddOutlined, Close } from "@mui/icons-material";
import { useScrollyTellProps, useScrollytellTranslation } from "../Admin/scrollytellContext";
import { updateSection } from "../Admin/service";
import { ImageFocusPicker } from "../common/ImageFocusPicker";
import { StickySectionSidebarStep } from "./StickySectionSidebarStep";
import {
  normalizeMultiBackgrounds,
  duplicateStickyStep,
  addStickyStep,
  deleteStickyStep,
  updateArrayOrder,
} from "./stickySectionHelpers";

const useStyles = createUseStyles({
  stickySectionSidebar: { width: "225px" },
  header: { display: "flex", justifyContent: "center", borderBottom: "2px solid #0000001F" },
  title: { fontSize: "14px", lineHeight: "21px", color: "#000000DE" },
  steps: { display: "flex", flexDirection: "column", borderBottom: "2px solid #0000001F" },
  addStepButtonWrapper: { padding: "16px", display: "flex", justifyContent: "center", borderBottom: "2px solid #0000001F" },
  imageFocusPicker: { top: "0", width: "250px", left: "-5px" },
});

export const StickySectionSidebar = ({ isOpen, onClose, section, sectionId }) => {
  const classes = useStyles();
  const t = useScrollytellTranslation();
  const { onMediaSelect, onChange, story } = useScrollyTellProps();
  const [focusPickerIndex, setFocusPickerIndex] = useState(null);

  const backgroundImages = normalizeMultiBackgrounds(section);
  const focusedImage = backgroundImages[focusPickerIndex];

  const addStep = () => {
    const result = addStickyStep(backgroundImages, section?.containers);
    onChange(updateSection(story, sectionId, result));
  };

  const updateMediaForStep = (i, type, dmsId) => {
    onChange(
      updateSection(story, sectionId, {
        backgroundImage: backgroundImages.map((m, index) =>
          index === i ? { [type]: dmsId } : m
        ),
      })
    );
  };

  const addBackgroundImageForStep = (i) => {
    if (onMediaSelect) onMediaSelect("image", (dmsId) => updateMediaForStep(i, "image", dmsId));
  };

  const addBackgroundVideoForStep = (i) => {
    if (onMediaSelect) onMediaSelect("video", (dmsId) => updateMediaForStep(i, "video", dmsId));
  };

  const moveStepUp = (i) => {
    onChange(
      updateSection(story, sectionId, {
        backgroundImage: updateArrayOrder(backgroundImages, i, i - 1),
        containers: updateArrayOrder(section?.containers, i, i - 1),
      })
    );
  };

  const moveStepDown = (i) => {
    onChange(
      updateSection(story, sectionId, {
        backgroundImage: updateArrayOrder(backgroundImages, i, i + 1),
        containers: updateArrayOrder(section?.containers, i, i + 1),
      })
    );
  };

  const duplicateStep = (i) => {
    const result = duplicateStickyStep(backgroundImages, section?.containers, i);
    onChange(updateSection(story, sectionId, result));
  };

  const deleteStep = (i) => {
    const result = deleteStickyStep(backgroundImages, section?.containers, i);
    onChange(updateSection(story, sectionId, result));
  };

  const setBackgroundFocus = (x, y) => {
    onChange(
      updateSection(story, sectionId, {
        backgroundImage: backgroundImages.map((img, index) =>
          index === focusPickerIndex ? { ...img, x, y } : img
        ),
      })
    );
  };

  const scrollToContainer = (containerId) => {
    const content = document.getElementById("scrollytell-content");
    if (content) {
      document
        .getElementById(`container-${sectionId}-${containerId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  return (
    <>
      <Drawer anchor="left" open={isOpen} onClose={onClose}>
        <Toolbar className={classes.header}>
          <div className={classes.title}>
            {t("stickySectionSidebar.header", "Bakgrunnsbilder per steg")}
          </div>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Toolbar>
        <div className={classes.stickySectionSidebar}>
          <div className={classes.steps}>
            {section?.containers.map((container, i) => (
              <StickySectionSidebarStep
                key={i}
                i={i}
                index={i}
                image={backgroundImages[i]}
                background={backgroundImages[i]}
                onScrollTo={scrollToContainer}
                onSelectStep={() => scrollToContainer(i)}
                onAddBackground={addBackgroundImageForStep}
                onAddImage={addBackgroundImageForStep}
                onAddVideoBackground={addBackgroundVideoForStep}
                onAddVideo={addBackgroundVideoForStep}
                onMoveUp={moveStepUp}
                onMoveDown={moveStepDown}
                onSetFocusPickerIndex={setFocusPickerIndex}
                onDuplicate={duplicateStep}
                onDelete={deleteStep}
                isLast={i >= (section?.containers?.length || 1) - 1}
              />
            ))}
          </div>
          <div className={classes.addStepButtonWrapper}>
            <Button
              onClick={addStep}
              sx={{ backgroundColor: "#E0E0E0", color: "#616161" }}
            >
              <AddOutlined />
              {t("stickySectionSidebar.addStepButtonTitle", "Legg til steg")}
            </Button>
          </div>
        </div>
      </Drawer>
      {focusPickerIndex !== null && focusedImage?.image && (
        <ImageFocusPicker
          imageUrl={focusedImage?.image}
          x={focusedImage?.x}
          y={focusedImage?.y}
          onFocusChanged={(focus) => setBackgroundFocus(focus.x, focus.y)}
          onClose={() => setFocusPickerIndex(null)}
          className={classes.imageFocusPicker}
        />
      )}
    </>
  );
};
